import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { snakeToCamel } from '@rossum/api-client/utils';
import Filename from '@rossum/components/filename/Filename';
import StatusChip from '@rossum/components/status/StatusChip';
import { IconCornerDownRight } from '@rossum/ui/icons/tabler';
import {
  Box,
  Link,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { constructDocumentUrl } from '../../../../lib/url';
import { dataDisabled } from '../../../../redux/modules/annotation/helpers';
import { userSelector } from '../../../../redux/modules/user/selectors';
import { disabledContainerStyles } from '../../../pricing/components/DisabledRow';
import { AttachmentT, isAnnotationDisabled } from './helpers';

type Props = {
  // Attachment with annotation present.
  attachment: AttachmentT & Pick<Required<AttachmentT>, 'annotation'>;

  isChild: boolean;
  onMouseEnter: (attachment: AttachmentT) => void;
  onMouseLeave: () => void;
};

const AnnotationAttachment = ({
  attachment,

  isChild,
  onMouseEnter,
  onMouseLeave,
}: Props) => {
  const intl = useIntl();
  const { id: userId } = useSelector(userSelector);
  const {
    annotation: { status, id },
  } = attachment;

  const statusTooltipTitle = intl.formatMessage(
    {
      id: `containers.annotationList.statuses.reviewing.tooltip`,
    },
    {
      modifier:
        attachment.annotationModifier?.username ??
        intl.formatMessage({
          id: 'containers.documents.unknownModifier',
        }),
    }
  );

  return (
    <ListItemButton
      disableRipple
      disableGutters
      sx={{
        py: 0,
        pl: 2,
        borderRadius: borderRadius('s'),
        ...(attachment.annotation.restrictedAccess
          ? disabledContainerStyles
          : {}),
      }}
    >
      {isChild && (
        <SvgIcon
          fontSize="medium"
          sx={{ color: theme => theme.palette.text.secondary }}
        >
          <IconCornerDownRight />
        </SvgIcon>
      )}
      <ListItemIcon sx={{ minWidth: 25, mr: 1 }}>
        {status !== 'purged' ? (
          <StatusChip
            status={snakeToCamel(status)}
            isOwned={attachment.annotationModifier?.id === userId}
            tooltipTitle={status === 'reviewing' ? statusTooltipTitle : null}
            disabled={attachment.annotation.restrictedAccess}
          />
        ) : null}
      </ListItemIcon>
      <ListItemText sx={{ my: 0 }}>
        <Link
          // TODO isAnnotationDisabled cursor
          onClick={e => isAnnotationDisabled(status) && e.preventDefault()}
          target="_blank"
          href={constructDocumentUrl({ id })}
          rel="noreferrer"
          underline="none"
          sx={{ '&:hover': { textDecoration: 'none' } }}
        >
          <Box
            onMouseEnter={() => onMouseEnter(attachment)}
            onMouseLeave={() => onMouseLeave()}
            sx={{ py: 1 }}
            data-cy="annotation-attachment-link"
          >
            <Typography
              variant="body2"
              color="text.primary"
              whiteSpace="nowrap"
              component="div"
            >
              <Filename
                status={attachment.annotation.status}
                disabled={dataDisabled.includes(status)}
                originalFilename={attachment.document.originalFileName}
              />
            </Typography>
          </Box>
        </Link>
      </ListItemText>
    </ListItemButton>
  );
};

export { AnnotationAttachment };
